/**
 * Turn titles picked in the chat into quiz draft rules.
 *  - Microlab bank: one rule for the catalogue limited to the chosen titles
 *    (`quiz_title_ids`; empty = the whole catalogue).
 *  - Private bank: one rule per chosen title (each rule holds one topic), with
 *    the question count split across them.
 */

const CATALOGUE_NAMES = { histology: "Histology", embryology: "Embryology", anatomy: "Anatomy" };

export function catalogueName(catalogue) {
  return CATALOGUE_NAMES[catalogue] || "Microlab";
}

function clampInt(value, min, max, fallback) {
  const n = Math.round(Number(value));
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

/** Split `total` across `parts` as evenly as possible (first ones get the remainder). */
export function splitCount(total, parts) {
  if (parts <= 0) return [];
  const base = Math.floor(total / parts);
  const extra = total % parts;
  return Array.from({ length: parts }, (_, i) => base + (i < extra ? 1 : 0));
}

export function buildRulesFromTitles({ source, catalogue, titles, numQuestions, wholeCatalogue = false }) {
  const total = clampInt(numQuestions, 1, 200, 10);
  if (source === "private") {
    const picked = (titles || []).slice(0, total); // never a rule with 0 questions
    const counts = splitCount(total, picked.length);
    return picked.map((t, i) => ({
      bank_source: "private",
      catalogue: "",
      professor_topic_id: t.id,
      professor_tile_id: "",
      quiz_title_ids: [],
      subtitle_labels: [],
      topic_label: t.title,
      num_questions: Math.min(50, counts[i]),
      difficulty_filter: "",
    }));
  }
  const list = wholeCatalogue ? [] : titles || [];
  return [
    {
      bank_source: "microlab",
      catalogue,
      professor_topic_id: "",
      professor_tile_id: "",
      quiz_title_ids: list.map((t) => t.id),
      subtitle_labels: list.map((t) => t.title),
      topic_label: catalogueName(catalogue),
      num_questions: Math.min(50, total),
      difficulty_filter: "",
    },
  ];
}

export function applyTitleSelection(draft, selection) {
  const rules = buildRulesFromTitles(selection);
  const isPrivate = selection.source === "private";
  const defaultTitle = isPrivate
    ? selection.titles?.length === 1
      ? `${selection.titles[0].title} Quiz`
      : "My Questions Quiz"
    : selection.titles?.length === 1 && !selection.wholeCatalogue
      ? `${selection.titles[0].title} Quiz`
      : `${catalogueName(selection.catalogue)} Quiz`;
  return {
    ...draft,
    title: String(draft?.title || "").trim() && !/^(histology|embryology|anatomy) quiz$/i.test(draft.title)
      ? draft.title
      : defaultTitle,
    time_limit_minutes: clampInt(selection.timeLimit, 1, 300, draft?.time_limit_minutes || 30),
    bank_preference: isPrivate ? "private" : "microlab",
    topic_rules: rules,
    awaiting_confirmation: false,
    confirmation_fingerprint: null,
  };
}
